// Badge and StatusBadge (DESIGN_SYSTEM §3.5, §9.2, §9.4). Tone + icon + text, never colour alone;
// radius 4; the icon is aria-hidden and the text is the accessible name; text wraps, never truncates.
import type { ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import type { Tone } from "../state";
import { toneClasses } from "../tones";
import { Icon, type IconName } from "./Icon";

const toneIcon: Record<Tone, IconName> = {
  neutral: "circle",
  action: "dot",
  transit: "arrow",
  success: "check",
  warning: "triangle",
  danger: "octagon",
};

export interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
  /** Defaults to the tone's icon; pass null for a plain label badge (e.g. counts). */
  icon?: IconName | null;
  strike?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function Badge({
  tone = "neutral",
  children,
  icon,
  strike,
  className,
  "data-testid": testId,
}: BadgeProps) {
  const iconName = icon === undefined ? toneIcon[tone] : icon;
  return (
    <span
      className={cx(
        "inline-flex max-w-full items-center gap-1 rounded-xs px-1.5 py-0.5 text-2xs font-medium",
        toneClasses[tone].badge,
        className,
      )}
      data-tone={tone}
      data-testid={testId}
    >
      {iconName ? <Icon name={iconName} size={14} className="flex-none" /> : null}
      <span className={cx(strike && "line-through")}>{children}</span>
    </span>
  );
}

// ---------------------------------------------------------------- domain mappings (§9.4)
export const ORDER_TONES = {
  unfinished: "neutral",
  new: "action",
  confirmed: "action",
  packed: "action",
  with_courier: "transit",
  delivered: "success",
  cancelled: "neutral",
  return_requested: "warning",
  returned: "danger",
} as const satisfies Record<string, Tone>;

export const PAYMENT_TONES = {
  unpaid: "neutral",
  cod_to_collect: "warning",
  awaiting_verification: "action",
  processing: "transit",
  partially_paid: "warning",
  paid: "success",
  settled: "success",
  failed: "danger",
  refunded: "neutral",
} as const satisfies Record<string, Tone>;

export const COURIER_TONES = {
  not_booked: "neutral",
  booked: "action",
  picked_up: "transit",
  in_transit: "transit",
  out_for_delivery: "transit",
  delivered: "success",
  attempt_failed: "warning",
  returning: "danger",
  returned: "danger",
} as const satisfies Record<string, Tone>;

export const VENDOR_TONES = {
  invited: "neutral",
  trial: "action",
  active: "success",
  past_due: "warning",
  suspended: "danger",
  offboarding: "warning",
  closed: "neutral",
} as const satisfies Record<string, Tone>;

export const JOB_TONES = {
  queued: "neutral",
  running: "transit",
  retrying: "warning",
  succeeded: "success",
  failed: "danger",
  dead_lettered: "danger",
  cancelled: "neutral",
} as const satisfies Record<string, Tone>;

const DOMAINS = {
  order: ORDER_TONES,
  payment: PAYMENT_TONES,
  courier: COURIER_TONES,
  vendor: VENDOR_TONES,
  job: JOB_TONES,
} as const;

export type StatusDomain = keyof typeof DOMAINS;
export type StatusOf<D extends StatusDomain> = keyof (typeof DOMAINS)[D] & string;

export interface StatusBadgeProps<D extends StatusDomain> {
  domain: D;
  status: StatusOf<D>;
  className?: string;
  "data-testid"?: string;
}

export function StatusBadge<D extends StatusDomain>({
  domain,
  status,
  className,
  "data-testid": testId,
}: StatusBadgeProps<D>) {
  const tones = DOMAINS[domain] as Record<string, Tone>;
  const tone = tones[status] ?? "neutral";
  // Every (domain, status) pair above has a catalogue entry without placeholders (tested).
  const label = (t as (key: string) => string)(`status.${domain}.${status}`);
  return (
    <Badge
      tone={tone}
      strike={domain === "order" && status === "cancelled"}
      className={className}
      data-testid={testId}
    >
      {label}
    </Badge>
  );
}
